import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, Clock, Star, X, CheckCircle2, Wrench, Inbox, Users, ChevronRight,
  Facebook, Instagram, MessageCircleMore, Mail, Globe, Phone, Calendar, Hash, Store,
  Hourglass, ExternalLink, Navigation, AlertCircle, Link2, MessageSquareText, Layers,
  LayoutDashboard, ImageOff,
} from "lucide-react";
import moment from "moment";
import Sidebar1 from "components/Sidebar1";
import { fetchWithToken } from "api/ApiHandler";
import DEfulatImg from "../../Assests/Images/DefulatLogo.png";
import { useDialog, DialogFrame, DialogSection } from "../Admindashboard/ui";
import { FONT_HEADING, formatDate, fromNow, handleAuthError, Field, EmptyState } from "./shared";
import { statusMeta, StatusBadge, BusinessAvatar } from "./ServiceProvider";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const formatTime = (value) => {
  if (!value) return "";
  const m = moment(value, "HH:mm");
  return m.isValid() ? m.format("hh:mm A") : value;
};

const getReviewerAvatar = (userId) => {
  const path = userId?.imagePath || userId?.image;
  if (!path || path === "image") return DEfulatImg;
  return path;
};

// Pulls lat/lng out of either the app's `locations[]` or a GeoJSON-style `businessLocation`.
const getCoords = (raw) => {
  const loc = raw.locations?.find((l) => l.isPrimary) || raw.locations?.[0];
  if (loc?.latitude != null && loc?.longitude != null) return { lat: Number(loc.latitude), lng: Number(loc.longitude) };
  const bl = raw.businessLocation;
  if (bl?.latitude != null && bl?.longitude != null) return { lat: Number(bl.latitude), lng: Number(bl.longitude) };
  if (Array.isArray(bl?.coordinates) && bl.coordinates.length === 2) return { lat: Number(bl.coordinates[1]), lng: Number(bl.coordinates[0]) };
  return null;
};

// Maps the real `/admin/service-provider-details/:providerId` shop shape onto
// the fields this page renders (working hours -> per-day timings, the flat
// `services` list grouped back under their `serviceType` categories, etc).
const normalizeShop = (raw) => {
  const timings = DAYS.map((day) => {
    const wh = raw.workingHours?.find((w) => w.day === day);
    if (!wh || !wh.isOpen) {
      return { day, closed: true, is24Hours: false, start: "", end: "" };
    }
    const is24Hours = wh.startTime === "00:00" && wh.endTime === "23:59";
    return { day, closed: false, is24Hours, start: formatTime(wh.startTime), end: formatTime(wh.endTime) };
  });

  const categoryMap = new Map();
  (raw.serviceType || []).forEach((st) => {
    if (st?._id) categoryMap.set(st._id, { id: st._id, name: st.name, services: [] });
  });
  (raw.services || []).forEach((svc, idx) => {
    const catId = svc.serviceTypeId?._id || svc.serviceName || `cat-${idx}`;
    if (!categoryMap.has(catId)) {
      categoryMap.set(catId, { id: catId, name: svc.serviceTypeId?.name || svc.serviceName || "Other", services: [] });
    }
    categoryMap.get(catId).services.push({
      id: `${catId}-${idx}`,
      name: svc.title || svc.serviceName || "Service",
      image: svc.serviceImage,
      description: svc.description || "",
      isAvailable: svc.isAvailable !== false,
    });
  });

  const categories = Array.from(categoryMap.values()).filter((c) => c.services.length > 0);

  return {
    id: raw._id,
    name: raw.shopName,
    image: raw.shopImage,
    location: raw.businessLocation?.address || raw.locations?.[0]?.address || "-",
    coords: getCoords(raw),
    createdAt: raw.createdAt,
    rating: raw.averageRating || 0,
    reviewsCount: raw.totalReviews || 0,
    totalCustomers: raw.totalCustomers || 0,
    orders: {
      total: raw.totalRequests || 0,
      completed: raw.completedRequests || 0,
      pending: raw.pendingRequests || 0,
    },
    timings,
    categories,
    servicesCount: categories.reduce((s, c) => s + c.services.length, 0),
    socialLinks: (raw.socialLinks || []).filter((l) => l.url?.trim()),
    reviews: (raw.reviews || []).map((r) => ({
      id: r._id,
      name: `${r.userId?.firstName || ""} ${r.userId?.lastName || ""}`.trim() || "Anonymous",
      avatar: getReviewerAvatar(r.userId),
      rating: r.rate || 0,
      comment: r.comment || "",
      date: r.createdAt,
    })),
  };
};

const getTodayName = () => DAYS[moment().isoWeekday() - 1];
const getTodayTiming = (shop) => shop.timings.find((t) => t.day === getTodayName());

// "always open" means every single day is marked 24 hours
const isAlwaysOpen = (shop) => shop.timings.length > 0 && shop.timings.every((t) => !t.closed && t.is24Hours);

const isOpenNow = (shop) => {
  const t = getTodayTiming(shop);
  if (!t || t.closed) return false;
  if (t.is24Hours) return true;
  if (!t.start || !t.end) return false;
  return moment().isBetween(moment(t.start, "hh:mm A"), moment(t.end, "hh:mm A"));
};

const openState = (shop) => {
  if (isAlwaysOpen(shop)) return { label: "Always open", cls: "bg-[#EA352B] text-[#fff]", dot: "#fff" };
  const t = getTodayTiming(shop);
  if (t?.is24Hours) return { label: "Open 24h today", cls: "bg-[#16A34A] text-[#fff]", dot: "#fff" };
  return isOpenNow(shop)
    ? { label: "Open now", cls: "bg-[#16A34A] text-[#fff]", dot: "#fff" }
    : { label: "Closed", cls: "bg-[#1B1B22cc] text-[#fff]", dot: "#9CA3AF" };
};

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

const SOCIAL = {
  website: { Icon: Globe, color: "#4A90E2", href: (u) => (/^https?:\/\//.test(u) ? u : `https://${u}`) },
  facebook: { Icon: Facebook, color: "#1877F2", href: (u) => (/^https?:\/\//.test(u) ? u : `https://${u}`) },
  instagram: { Icon: Instagram, color: "#E4405F", href: (u) => (/^https?:\/\//.test(u) ? u : `https://instagram.com/${u.replace(/^@/, "")}`) },
  whatsapp: { Icon: MessageCircleMore, color: "#25D366", href: (u) => (/^https?:\/\//.test(u) ? u : `https://wa.me/${u.replace(/[^\d]/g, "")}`) },
  email: { Icon: Mail, color: "#6B7280", href: (u) => `mailto:${u}` },
};

export default function ServiceProviderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [provider, setProvider] = useState(null);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const closeShop = useCallback(() => setSelectedShop(null), []);

  useEffect(() => {
    let cancelled = false;
    const loadProviderDetails = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const data = await fetchWithToken(`/admin/service-provider-details/${id}`);
        if (cancelled) return;
        setProvider(data.provider);
        setShops(Array.isArray(data.shops) ? data.shops.map(normalizeShop) : []);
      } catch (error) {
        console.error("Fetch provider details error:", error.response?.data || error);
        if (handleAuthError(error)) return;
        if (!cancelled) setLoadError("Unable to load provider details.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadProviderDetails();
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  // Totals across all of this provider's shops
  const totals = useMemo(() => {
    const sum = (fn) => shops.reduce((s, x) => s + fn(x), 0);
    const reviews = sum((s) => s.reviewsCount);
    const rated = shops.filter((s) => s.rating > 0);
    const weighted = reviews
      ? sum((s) => s.rating * s.reviewsCount) / reviews
      : rated.length ? rated.reduce((a, s) => a + s.rating, 0) / rated.length : 0;
    const total = sum((s) => s.orders.total);
    const completed = sum((s) => s.orders.completed);
    return {
      customers: sum((s) => s.totalCustomers),
      requests: total,
      completed,
      pending: sum((s) => s.orders.pending),
      completionRate: pct(completed, total),
      services: sum((s) => s.servicesCount),
      reviews,
      rating: weighted,
    };
  }, [shops]);

  const allReviews = useMemo(
    () => shops
      .flatMap((s) => s.reviews.map((r) => ({ ...r, shopName: s.name })))
      .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [shops]
  );

  const status = statusMeta(provider?.status);
  const phone = provider?.phNumber || provider?.phone || provider?.businessPhone;

  return (
    <div className="flex h-screen bg-[#F6F6F8] overflow-hidden">
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      <main className="flex-1 min-w-0 overflow-y-auto px-8 py-6 lg:px-6 md:px-4 sm:px-3">
        {/* Breadcrumb */}
        <div className="flex items-center gap-3 mb-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="w-10 h-10 rounded-xl bg-[#fff] border border-[#ECECEF] flex items-center justify-center shrink-0 hover:border-[#EA352B] hover:text-[#EA352B] text-[#374151] transition"
          >
            <ArrowLeft size={18} />
          </button>
          <nav className="flex items-center gap-1.5 text-sm min-w-0" aria-label="Breadcrumb">
            <Link to="/user-management/service-provider" className="no-underline text-[#6B7280] hover:text-[#EA352B] whitespace-nowrap">Service Providers</Link>
            <ChevronRight size={14} className="text-[#C0C4CC] shrink-0" />
            <span className="text-[#111] font-medium truncate">{isLoading ? "Loading…" : provider?.businessName || "Provider"}</span>
          </nav>
        </div>

        {loadError ? (
          <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1]">
            <EmptyState
              Icon={AlertCircle}
              title={loadError}
              text="Check your connection and try again."
              action={<button type="button" onClick={() => setReloadKey((k) => k + 1)} className="mt-3 rounded-xl bg-[#EA352B] text-[#fff] px-4 py-2 text-sm font-medium">Try again</button>}
            />
          </div>
        ) : (
          <>
            {/* Provider hero */}
            <section className="bg-[#fff] rounded-3xl border border-[#EEEEF1] overflow-hidden mb-6" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
              <div
                className="relative overflow-hidden px-6 py-6 sm:px-4 sm:py-5"
                style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 55%, #EA352B 135%)" }}
              >
                <div className="pointer-events-none absolute -right-16 -top-24 w-72 h-72 rounded-full bg-[#EA352B] opacity-20" />
                <div className="pointer-events-none absolute right-56 -bottom-28 w-56 h-56 rounded-full bg-[#FF8A3D] opacity-10" />

                {isLoading ? (
                  <div className="relative flex items-center gap-4">
                    <div className="w-[72px] h-[72px] rounded-2xl bg-[#ffffff1f] animate-pulse" />
                    <div className="space-y-2">
                      <div className="h-5 w-48 bg-[#ffffff1f] rounded animate-pulse" />
                      <div className="h-3 w-32 bg-[#ffffff1a] rounded animate-pulse" />
                    </div>
                  </div>
                ) : (
                  <div className="relative flex items-center justify-between gap-5 flex-wrap">
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="rounded-[20px] p-1 bg-[#ffffff1f] shrink-0">
                        <BusinessAvatar name={provider?.businessName} size={72} />
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <h1 className="m-0 text-2xl sm:text-xl font-medium text-[#fff] truncate" style={FONT_HEADING}>
                            {provider?.businessName || "Unnamed business"}
                          </h1>
                          <StatusBadge meta={status} />
                        </div>
                        <p className="m-0 mt-1.5 text-sm text-[#ffffffb3] flex items-center gap-x-3 gap-y-1 flex-wrap">
                          {provider?.fullName && (
                            <span className="inline-flex items-center gap-1.5">
                              <Users size={14} /> {provider.fullName}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar size={14} /> Joined {fromNow(provider?.createdAt)}
                          </span>
                          {!isLoading && (
                            <span className="inline-flex items-center gap-1.5">
                              <Store size={14} /> {shops.length} {shops.length === 1 ? "shop" : "shops"}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {provider?.businessEmail && (
                        <a
                          href={`mailto:${provider.businessEmail}`}
                          className="no-underline inline-flex items-center gap-1.5 rounded-xl bg-[#fff] text-[#111] px-4 py-2.5 text-xs font-semibold hover:bg-[#FDECEA] transition"
                        >
                          <Mail size={14} className="text-[#EA352B]" /> Email
                        </a>
                      )}
                      {phone && (
                        <a
                          href={`tel:${phone}`}
                          className="no-underline inline-flex items-center gap-1.5 rounded-xl bg-[#ffffff1f] text-[#fff] px-4 py-2.5 text-xs font-semibold hover:bg-[#ffffff33] transition"
                        >
                          <Phone size={14} /> Call
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-2 p-4 sm:p-3">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-[58px] rounded-xl bg-[#F3F3F6] animate-pulse" />)
                ) : (
                  <>
                    <Field Icon={Mail} label="Business email" value={provider?.businessEmail} copy />
                    <Field Icon={Phone} label="Phone" value={phone} copy />
                    <Field Icon={Calendar} label="Joined" value={formatDate(provider?.createdAt, true)} />
                    <Field Icon={Hash} label="Provider ID" value={provider?._id || id} copy mono />
                  </>
                )}
              </div>
            </section>

            {/* Totals */}
            <section className="grid grid-cols-6 lg:grid-cols-3 sm:grid-cols-2 gap-4 mb-6">
              <Metric Icon={Store} label="Shops" value={shops.length} loading={isLoading} />
              <Metric Icon={Layers} label="Services" value={totals.services} loading={isLoading} />
              <Metric Icon={Users} label="Customers served" value={totals.customers} loading={isLoading} />
              <Metric Icon={Inbox} label="Total requests" value={totals.requests} loading={isLoading} />
              <Metric Icon={CheckCircle2} label="Completed" value={totals.completed} sub={`${totals.completionRate}% completion`} tone="green" loading={isLoading} />
              <Metric Icon={Star} label="Avg rating" value={totals.rating ? totals.rating.toFixed(1) : "–"} sub={`${totals.reviews} reviews`} tone="amber" loading={isLoading} />
            </section>

            <div className="grid grid-cols-[minmax(0,1fr)_340px] lg:grid-cols-[minmax(0,1fr)_300px] md:grid-cols-1 gap-6 md:gap-4">
              {/* Shops */}
              <section className="min-w-0 space-y-6 md:space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="m-0 text-xl font-medium text-[#111]" style={FONT_HEADING}>
                    Shops {!isLoading && <span className="text-[#9CA3AF]">· {shops.length}</span>}
                  </h2>
                  {!isLoading && shops.length > 0 && <span className="text-xs text-[#9CA3AF]">Click a shop for full details</span>}
                </div>

                {isLoading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-1 gap-5">
                    {Array.from({ length: 2 }).map((_, i) => <ShopCardSkeleton key={i} />)}
                  </div>
                ) : shops.length === 0 ? (
                  <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1]">
                    <EmptyState Icon={Store} title="No shops yet" text="This provider hasn’t created a shop in the app." />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-1 gap-5 md:gap-4">
                    {shops.map((shop) => <ShopCard key={shop.id} shop={shop} onOpen={() => setSelectedShop(shop)} />)}
                  </div>
                )}

                {!isLoading && shops.length > 1 && <ShopComparison shops={shops} onOpen={setSelectedShop} />}
              </section>

              {/* Side column */}
              <aside className="min-w-0 space-y-6 md:space-y-0 md:grid md:grid-cols-2 md:gap-4 sm:grid-cols-1">
                <SidePanel title="Requests" Icon={Inbox}>
                  {isLoading ? <div className="h-28 rounded-xl bg-[#F3F3F6] animate-pulse" /> : <RequestsBreakdown totals={totals} />}
                </SidePanel>
                <SidePanel title="Ratings" Icon={Star}>
                  {isLoading ? <div className="h-36 rounded-xl bg-[#F3F3F6] animate-pulse" /> : <RatingBreakdown reviews={allReviews} rating={totals.rating} count={totals.reviews} />}
                </SidePanel>
                <SidePanel title="Latest reviews" Icon={MessageSquareText}>
                  {isLoading ? (
                    <div className="h-36 rounded-xl bg-[#F3F3F6] animate-pulse" />
                  ) : allReviews.length === 0 ? (
                    <p className="m-0 text-sm text-[#9CA3AF] text-center py-4">No reviews yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {allReviews.slice(0, 3).map((r) => <ReviewItem key={r.id} review={r} showShop compact />)}
                    </div>
                  )}
                </SidePanel>
              </aside>
            </div>
          </>
        )}
      </main>

      <ShopDialog shop={selectedShop} onClose={closeShop} />
    </div>
  );
}

// ─── Page pieces ────────────────────────────────────────────────────────────

const TONES = {
  red: { bg: "#FDECEA", fg: "#EA352B" },
  green: { bg: "#DCFCE7", fg: "#16A34A" },
  amber: { bg: "#FEF3C7", fg: "#D97706" },
  blue: { bg: "#DBEAFE", fg: "#2563EB" },
  gray: { bg: "#F3F4F6", fg: "#4B5563" },
};

function Metric({ Icon, label, value, sub, tone = "red", loading }) {
  const t = TONES[tone];
  return (
    <div className="bg-[#fff] rounded-2xl border border-[#EEEEF1] p-4 min-w-0" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
      <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: t.bg }}>
        <Icon size={16} style={{ color: t.fg }} className={tone === "amber" ? "fill-current" : ""} />
      </span>
      <p className="m-0 mt-3 text-[11px] font-medium text-[#6B7280]">{label}</p>
      {loading ? (
        <div className="h-6 w-12 mt-1 rounded bg-[#F3F3F6] animate-pulse" />
      ) : (
        <p className="m-0 text-xl font-semibold text-[#111]">{typeof value === "number" ? value.toLocaleString() : value}</p>
      )}
      {!loading && sub && <p className="m-0 text-[11px] text-[#9CA3AF]">{sub}</p>}
    </div>
  );
}

function SidePanel({ title, Icon, children }) {
  return (
    <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-5 min-w-0" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg bg-[#FDECEA] flex items-center justify-center"><Icon size={15} className="text-[#EA352B]" /></span>
        <h3 className="m-0 text-base font-medium text-[#111]" style={FONT_HEADING}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function ShopCover({ src, name, className = "" }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (src && !failed) {
    return <img src={src} alt={name || ""} onError={() => setFailed(true)} className={`w-full h-full object-cover ${className}`} />;
  }
  return (
    <div className="w-full h-full relative" style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 55%, #EA352B 130%)" }}>
      <div className="absolute -right-8 -top-10 w-40 h-40 rounded-full bg-[#EA352B] opacity-25" />
      <Store size={72} className="absolute right-5 bottom-3 text-[#ffffff1a]" />
    </div>
  );
}

function OpenPill({ shop }) {
  const s = openState(shop);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur ${s.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} /> {s.label}
    </span>
  );
}

function ShopCard({ shop, onOpen }) {
  const done = pct(shop.orders.completed, shop.orders.total);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group text-left bg-[#fff] rounded-3xl border border-[#EEEEF1] overflow-hidden hover:shadow-[0_12px_32px_rgba(17,17,26,0.08)] hover:-translate-y-0.5 transition-all"
    >
      <div className="relative h-40 overflow-hidden">
        <ShopCover src={shop.image} name={shop.name} className="group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 right-3"><OpenPill shop={shop} /></div>
        <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#fff] px-2.5 py-1 text-xs font-semibold text-[#111] shadow-sm">
          <Star size={12} className="text-[#F5B400] fill-[#F5B400]" /> {shop.rating ? shop.rating.toFixed(1) : "New"}
          <span className="font-normal text-[#9CA3AF]">({shop.reviewsCount})</span>
        </div>
      </div>
      <div className="p-5 sm:p-4">
        <h3 className="m-0 text-base font-medium text-[#111] truncate group-hover:text-[#EA352B] transition-colors" style={FONT_HEADING}>{shop.name}</h3>
        <p className="m-0 mt-1 text-xs text-[#6B7280] flex items-center gap-1.5 min-w-0">
          <MapPin size={13} className="text-[#EA352B] shrink-0" /> <span className="truncate">{shop.location}</span>
        </p>

        <div className="grid grid-cols-3 gap-2 mt-4">
          <MiniTile label="Services" value={shop.servicesCount} />
          <MiniTile label="Customers" value={shop.totalCustomers} />
          <MiniTile label="Requests" value={shop.orders.total} />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] mb-1.5">
            <span className="text-[#6B7280]">{shop.orders.completed} completed · {shop.orders.pending} pending</span>
            <span className="font-semibold text-[#111]">{done}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#F1F1F4] overflow-hidden">
            <div className="h-full rounded-full bg-[#16A34A]" style={{ width: `${done}%` }} />
          </div>
        </div>

        {shop.categories.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-4">
            {shop.categories.slice(0, 3).map((c) => (
              <span key={c.id} className="rounded-full bg-[#F3F3F6] text-[#4B5563] px-2.5 py-1 text-[11px] font-medium">{c.name}</span>
            ))}
            {shop.categories.length > 3 && (
              <span className="rounded-full bg-[#FDECEA] text-[#EA352B] px-2.5 py-1 text-[11px] font-semibold">+{shop.categories.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function MiniTile({ label, value }) {
  return (
    <div className="rounded-xl bg-[#F7F7F9] px-2.5 py-2 text-center">
      <p className="m-0 text-base font-semibold text-[#111] leading-tight">{Number(value || 0).toLocaleString()}</p>
      <p className="m-0 text-[10px] text-[#9CA3AF]">{label}</p>
    </div>
  );
}

function ShopComparison({ shops, onOpen }) {
  return (
    <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 md:p-5 sm:p-4" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
      <h3 className="m-0 mb-4 text-base font-medium text-[#111]" style={FONT_HEADING}>Shop comparison</h3>
      <div className="overflow-x-auto -mx-6 md:-mx-5 sm:-mx-4 px-6 md:px-5 sm:px-4">
        <table className="w-full min-w-[560px] text-left border-separate border-spacing-0">
          <thead>
            <tr>
              {["Shop", "Rating", "Customers", "Requests", "Completion"].map((h) => (
                <th key={h} className="py-2.5 px-3 first:pl-0 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF] border-b border-[#EEEEF1] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shops.map((s) => {
              const done = pct(s.orders.completed, s.orders.total);
              return (
                <tr key={s.id} onClick={() => onOpen(s)} className="cursor-pointer hover:bg-[#FFF7F6] transition-colors">
                  <td className="py-3 pr-3 border-b border-[#F3F3F6] text-sm font-medium text-[#111] whitespace-nowrap">{s.name}</td>
                  <td className="py-3 px-3 border-b border-[#F3F3F6] text-sm text-[#374151] whitespace-nowrap">
                    <span className="inline-flex items-center gap-1"><Star size={12} className="text-[#F5B400] fill-[#F5B400]" /> {s.rating ? s.rating.toFixed(1) : "–"}</span>
                  </td>
                  <td className="py-3 px-3 border-b border-[#F3F3F6] text-sm text-[#374151]">{s.totalCustomers}</td>
                  <td className="py-3 px-3 border-b border-[#F3F3F6] text-sm text-[#374151]">{s.orders.total}</td>
                  <td className="py-3 px-3 border-b border-[#F3F3F6] w-40">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-[#F1F1F4] overflow-hidden">
                        <div className="h-full rounded-full bg-[#16A34A]" style={{ width: `${done}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-[#111] w-9 text-right">{done}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RequestsBreakdown({ totals, orders }) {
  const t = orders
    ? { requests: orders.total, completed: orders.completed, pending: orders.pending }
    : totals;
  const other = Math.max(0, t.requests - t.completed - t.pending);
  const parts = [
    { label: "Completed", value: t.completed, color: "#16A34A" },
    { label: "Pending", value: t.pending, color: "#F59E0B" },
    { label: "Other", value: other, color: "#D1D5DB", hint: "Rejected or cancelled" },
  ];
  return (
    <div>
      <div className="flex items-end justify-between">
        <span className="text-3xl font-semibold text-[#111]">{t.requests.toLocaleString()}</span>
        <span className="text-xs text-[#6B7280]">total requests</span>
      </div>
      <div className="h-2.5 rounded-full bg-[#F1F1F4] overflow-hidden mt-3 flex">
        {parts.map((p) => (
          <div key={p.label} className="h-full" style={{ width: `${pct(p.value, t.requests)}%`, background: p.color }} />
        ))}
      </div>
      <div className="mt-3 space-y-1.5">
        {parts.map((p) => (
          <div key={p.label} className="flex items-center justify-between text-xs">
            <span className="inline-flex items-center gap-2 text-[#374151]" title={p.hint}>
              <span className="w-2 h-2 rounded-full" style={{ background: p.color }} /> {p.label}
            </span>
            <span className="text-[#6B7280]"><b className="text-[#111]">{p.value.toLocaleString()}</b> · {pct(p.value, t.requests)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stars({ value, size = 13 }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} className={i < Math.round(value) ? "text-[#F5B400] fill-[#F5B400]" : "text-[#E5E7EB] fill-[#E5E7EB]"} />
      ))}
    </span>
  );
}

function RatingBreakdown({ reviews, rating, count }) {
  const buckets = [5, 4, 3, 2, 1].map((n) => ({ n, c: reviews.filter((r) => Math.round(r.rating) === n).length }));
  const max = Math.max(1, ...buckets.map((b) => b.c));
  return (
    <div className="flex gap-4">
      <div className="text-center shrink-0">
        <p className="m-0 text-4xl font-semibold text-[#111] leading-none">{rating ? rating.toFixed(1) : "–"}</p>
        <div className="mt-2"><Stars value={rating} size={12} /></div>
        <p className="m-0 mt-1 text-[11px] text-[#9CA3AF]">{count} reviews</p>
      </div>
      <div className="flex-1 space-y-1.5 min-w-0">
        {buckets.map((b) => (
          <div key={b.n} className="flex items-center gap-2 text-[11px]">
            <span className="w-3 text-[#6B7280]">{b.n}</span>
            <div className="flex-1 h-1.5 rounded-full bg-[#F1F1F4] overflow-hidden">
              <div className="h-full rounded-full bg-[#F5B400]" style={{ width: `${(b.c / max) * 100}%` }} />
            </div>
            <span className="w-5 text-right text-[#9CA3AF]">{b.c}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewItem({ review, showShop, compact }) {
  return (
    <div className={`rounded-2xl ${compact ? "bg-[#F7F7F9] p-3" : "border border-[#EEEEF1] bg-[#fff] p-4"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <img src={review.avatar} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-8 h-8 rounded-full object-cover shrink-0" alt="" />
          <div className="min-w-0">
            <p className="m-0 text-sm font-medium text-[#111] truncate">{review.name}</p>
            <p className="m-0 text-[11px] text-[#9CA3AF] truncate">{showShop ? `${review.shopName} · ` : ""}{fromNow(review.date)}</p>
          </div>
        </div>
        <Stars value={review.rating} size={12} />
      </div>
      {review.comment && (
        <p className={`m-0 mt-2 text-[13px] text-[#4B5563] leading-relaxed ${compact ? "line-clamp-2" : ""}`}>{review.comment}</p>
      )}
    </div>
  );
}

function ShopCardSkeleton() {
  return (
    <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] overflow-hidden">
      <div className="h-40 bg-[#F3F3F6] animate-pulse" />
      <div className="p-5 space-y-3">
        <div className="h-4 w-36 bg-[#F3F3F6] rounded animate-pulse" />
        <div className="h-3 w-48 bg-[#F3F3F6] rounded animate-pulse" />
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-[#F3F3F6] rounded-xl animate-pulse" />)}
        </div>
      </div>
    </div>
  );
}

// ─── Shop dialog ────────────────────────────────────────────────────────────

const TABS = [
  { key: "overview", label: "Overview", Icon: LayoutDashboard },
  { key: "services", label: "Services", Icon: Wrench },
  { key: "reviews", label: "Reviews", Icon: MessageSquareText },
];

function ShopDialog({ shop, onClose }) {
  const closeRef = useRef(null);
  const [tab, setTab] = useState("overview");
  const [category, setCategory] = useState("all");

  useDialog(!!shop, onClose);
  useEffect(() => {
    if (!shop) return;
    setTab("overview");
    setCategory("all");
    closeRef.current?.focus();
  }, [shop]);

  if (!shop) return null;

  const counts = { overview: null, services: shop.servicesCount, reviews: shop.reviews.length };
  const visibleCats = category === "all" ? shop.categories : shop.categories.filter((c) => c.id === category);

  return (
    <DialogFrame labelledBy="sp-shop-title" onClose={onClose} maxWidth={920}>
      {/* Cover */}
      <div className="relative shrink-0 h-44 sm:h-36 overflow-hidden">
        <ShopCover src={shop.image} name={shop.name} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,15,20,0.1) 0%, rgba(15,15,20,0.85) 100%)" }} />
        <button
          ref={closeRef}
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute z-10 top-4 right-4 w-9 h-9 rounded-xl bg-[#0000004d] hover:bg-[#00000080] backdrop-blur flex items-center justify-center transition"
        >
          <X size={18} className="text-[#fff]" />
        </button>
        <div className="absolute left-6 right-6 bottom-4 sm:left-4 sm:right-4 text-[#fff]">
          <div className="flex items-center gap-2 flex-wrap">
            <OpenPill shop={shop} />
            <span className="inline-flex items-center gap-1 rounded-full bg-[#fff] px-2.5 py-1 text-[11px] font-semibold text-[#111]">
              <Star size={11} className="text-[#F5B400] fill-[#F5B400]" /> {shop.rating ? shop.rating.toFixed(1) : "New"} ({shop.reviewsCount})
            </span>
          </div>
          <h2 id="sp-shop-title" className="m-0 mt-2 text-2xl sm:text-xl font-medium truncate" style={FONT_HEADING}>{shop.name}</h2>
          <p className="m-0 mt-0.5 text-xs text-[#ffffffcc] flex items-center gap-1.5 min-w-0">
            <MapPin size={13} className="shrink-0" /> <span className="truncate">{shop.location}</span>
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="shrink-0 flex gap-1 px-4 pt-3 bg-[#fff] border-b border-[#EEEEF1] overflow-x-auto" role="tablist">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition ${
              tab === key ? "border-[#EA352B] text-[#EA352B]" : "border-transparent text-[#6B7280] hover:text-[#111]"
            }`}
          >
            <Icon size={15} /> {label}
            {counts[key] != null && (
              <span className={`rounded-full px-1.5 text-[10px] font-semibold ${tab === key ? "bg-[#FDECEA]" : "bg-[#F3F3F6]"}`}>{counts[key]}</span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-4">
        {tab === "overview" && (
          <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
            <div className="col-span-2 md:col-span-1 grid grid-cols-4 sm:grid-cols-2 gap-3">
              <Metric Icon={Users} label="Customers" value={shop.totalCustomers} tone="blue" />
              <Metric Icon={Inbox} label="Total requests" value={shop.orders.total} tone="gray" />
              <Metric Icon={CheckCircle2} label="Completed" value={shop.orders.completed} sub={`${pct(shop.orders.completed, shop.orders.total)}%`} tone="green" />
              <Metric Icon={Hourglass} label="Pending" value={shop.orders.pending} tone="amber" />
            </div>

            <DialogSection title="Business hours">
              {isAlwaysOpen(shop) && (
                <div className="flex items-center gap-2 rounded-xl bg-[#FDECEA] text-[#EA352B] px-3 py-2.5 text-sm font-medium mb-2">
                  <CheckCircle2 size={15} /> Open 24 hours, every day
                </div>
              )}
              <ul className="m-0 pl-0 list-none space-y-1.5">
                {shop.timings.map((t) => {
                  const today = t.day === getTodayName();
                  return (
                    <li key={t.day} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm ${today ? "bg-[#FFF7F6] border border-[#F6C9C5]" : "bg-[#F7F7F9] border border-transparent"}`}>
                      <span className={`inline-flex items-center gap-2 ${t.closed ? "text-[#9CA3AF]" : "text-[#111]"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${t.closed ? "bg-[#D1D5DB]" : "bg-[#16A34A]"}`} />
                        {t.day}
                        {today && <span className="text-[10px] font-semibold text-[#EA352B]">Today</span>}
                      </span>
                      {t.closed ? (
                        <span className="text-xs text-[#9CA3AF]">Closed</span>
                      ) : t.is24Hours ? (
                        <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-[#ECFDF5] text-[#059669]">Open 24 hours</span>
                      ) : (
                        <span className="text-xs font-medium text-[#374151] inline-flex items-center gap-1"><Clock size={12} className="text-[#9CA3AF]" /> {t.start} – {t.end}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </DialogSection>

            <div className="space-y-4 min-w-0">
              <DialogSection
                title="Location"
                action={shop.coords && (
                  <a
                    href={`https://www.google.com/maps?q=${shop.coords.lat},${shop.coords.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="no-underline inline-flex items-center gap-1 text-xs font-semibold text-[#EA352B] hover:text-[#C92A21]"
                  >
                    Open in Maps <ExternalLink size={12} />
                  </a>
                )}
              >
                {shop.coords && (
                  <div className="rounded-2xl overflow-hidden border border-[#EEEEF1] h-40 bg-[#F1F1F4] mb-3">
                    <iframe
                      title={`Map of ${shop.name}`}
                      src={`https://maps.google.com/maps?q=${shop.coords.lat},${shop.coords.lng}&z=15&output=embed`}
                      className="w-full h-full border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <MapPin size={15} className="text-[#EA352B] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="m-0 text-sm text-[#111]">{shop.location}</p>
                    {shop.coords && (
                      <p className="m-0 mt-0.5 text-[11px] text-[#9CA3AF] inline-flex items-center gap-1">
                        <Navigation size={11} /> {shop.coords.lat.toFixed(4)}, {shop.coords.lng.toFixed(4)}
                      </p>
                    )}
                  </div>
                </div>
              </DialogSection>

              <DialogSection title="Social links">
                {shop.socialLinks.length === 0 ? (
                  <p className="m-0 text-sm text-[#9CA3AF]">No links added.</p>
                ) : (
                  <div className="space-y-2">
                    {shop.socialLinks.map((l) => {
                      const cfg = SOCIAL[(l.type || "").toLowerCase()] || { Icon: Link2, color: "#6B7280", href: (u) => u };
                      return (
                        <a
                          key={`${l.type}-${l.url}`}
                          href={cfg.href(l.url.trim())}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="no-underline group flex items-center gap-3 rounded-xl border border-[#EEEEF1] px-3 py-2 hover:border-[#D9DAE0] hover:shadow-sm transition min-w-0"
                        >
                          <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${cfg.color}1f` }}>
                            <cfg.Icon size={15} style={{ color: cfg.color }} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[11px] text-[#9CA3AF] capitalize">{l.title || l.type}</span>
                            <span className="block text-[13px] font-medium text-[#111] truncate">{l.url}</span>
                          </span>
                          <ExternalLink size={13} className="text-[#C0C4CC] group-hover:text-[#EA352B] shrink-0" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </DialogSection>
            </div>
          </div>
        )}

        {tab === "services" && (
          shop.categories.length === 0 ? (
            <EmptyState Icon={Wrench} title="No services yet" text="This shop hasn’t added any services." />
          ) : (
            <div>
              {shop.categories.length > 1 && (
                <div className="flex gap-2 flex-wrap mb-5">
                  {[{ id: "all", name: "All", services: { length: shop.servicesCount } }, ...shop.categories].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCategory(c.id)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${category === c.id ? "bg-[#EA352B] text-[#fff]" : "bg-[#fff] border border-[#EEEEF1] text-[#6B7280] hover:text-[#111]"}`}
                    >
                      {c.name} <span className="opacity-70">{c.services.length}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="space-y-6">
                {visibleCats.map((cat) => (
                  <div key={cat.id}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-7 h-7 rounded-lg bg-[#FDECEA] flex items-center justify-center"><Wrench size={13} className="text-[#EA352B]" /></span>
                      <span className="text-sm font-semibold text-[#111]">{cat.name}</span>
                      <span className="text-[11px] text-[#9CA3AF]">{cat.services.length} services</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-1 gap-3">
                      {cat.services.map((svc) => <ServiceCard key={svc.id} service={svc} />)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        {tab === "reviews" && (
          shop.reviews.length === 0 ? (
            <EmptyState Icon={Star} title="No reviews yet" text="Reviews from customers will show up here." />
          ) : (
            <div className="grid grid-cols-[260px_minmax(0,1fr)] md:grid-cols-1 gap-4 items-start">
              <DialogSection title="Summary">
                <RatingBreakdown reviews={shop.reviews} rating={shop.rating} count={shop.reviewsCount} />
              </DialogSection>
              <div className="space-y-3">
                {[...shop.reviews].sort((a, b) => new Date(b.date) - new Date(a.date)).map((r) => <ReviewItem key={r.id} review={r} />)}
              </div>
            </div>
          )
        )}
      </div>
    </DialogFrame>
  );
}

function ServiceCard({ service }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`flex gap-3 rounded-2xl border p-3 ${service.isAvailable ? "border-[#EEEEF1] bg-[#fff]" : "border-dashed border-[#E4E4E9] bg-[#FAFAFB]"}`}>
      <span className="w-16 h-16 rounded-xl overflow-hidden bg-[#F3F3F6] flex items-center justify-center shrink-0">
        {service.image && !failed ? (
          <img src={service.image} alt="" onError={() => setFailed(true)} className="w-full h-full object-cover" />
        ) : (
          <ImageOff size={18} className="text-[#C0C4CC]" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={`m-0 text-sm font-medium ${service.isAvailable ? "text-[#111]" : "text-[#9CA3AF]"}`}>{service.name}</p>
          {!service.isAvailable && <span className="shrink-0 text-[10px] font-semibold text-[#9CA3AF]">Unavailable</span>}
        </div>
        {service.description && <p className="m-0 mt-0.5 text-xs text-[#6B7280] leading-snug line-clamp-3">{service.description}</p>}
      </div>
    </div>
  );
}
